import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', padding: '4px 0' };
const smallBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--color-error)', cursor: 'pointer', fontSize: 'var(--font-size-sm)', padding: 0 };
const input: React.CSSProperties = { flex: 1, minHeight: 38, padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-background)', color: 'var(--color-text)', boxSizing: 'border-box', fontSize: 'var(--font-size-sm)' };
const voiceBtn: React.CSSProperties = { padding: 'var(--spacing-1) var(--spacing-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'transparent', color: 'var(--color-text)', fontWeight: 600, fontSize: 'var(--font-size-xs)', cursor: 'pointer' };

/** Editable scope-of-works checklist for a job, shown in the calendar job modal.
 *  Add line by line, tick off on site, remove. Used by admins + subcontractors. */
export function JobScope({ eventId, kind = 'scope', title = '📋 Scope of works' }: { eventId: string; kind?: 'scope' | 'defect'; title?: string }) {
  const qc = useQueryClient();
  const key = ['job-scope', eventId, kind];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => api.getJobScope(eventId, kind), enabled: Boolean(eventId) });
  const items = data?.items ?? [];
  const [text, setText] = useState('');
  const set = (d: { items: typeof items }) => qc.setQueryData(key, d);
  // Adding scope auto-starts the job server-side; refresh job-state so the
  // office's timing display picks up the start time.
  const touchState = () => { if (kind === 'scope') qc.invalidateQueries({ queryKey: ['job-state', eventId] }); };
  const add = useMutation({ mutationFn: (b: string) => api.addJobScopeItem(eventId, b, kind), onSuccess: (d) => { set(d); setText(''); touchState(); } });
  const upd = useMutation({ mutationFn: (v: { id: number; patch: { body?: string; done?: boolean } }) => api.updateJobScopeItem(eventId, v.id, v.patch, kind), onSuccess: set });
  const del = useMutation({ mutationFn: (id: number) => api.removeJobScopeItem(eventId, id, kind), onSuccess: set });
  const done = items.filter((i) => i.done).length;
  const noun = kind === 'defect' ? 'defect' : 'scope item';

  // Voice → scope (record a note, transcribe via Whisper, add the items).
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceErr, setVoiceErr] = useState<string | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startRec = async () => {
    setVoiceErr(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        setTranscribing(true);
        try {
          const d = await api.addJobScopeFromVoice(eventId, await blobToBase64(blob), blob.type);
          set(d);
          touchState();
          if (!d.added) setVoiceErr('Didn’t catch any scope items — try again, speaking each task clearly.');
        } catch (e: any) { setVoiceErr(e?.message || 'Could not transcribe the recording.'); }
        finally { setTranscribing(false); }
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
    } catch { setVoiceErr('Microphone access is blocked or unavailable on this device.'); }
  };
  const stopRec = () => { try { recRef.current?.stop(); } catch { /* ignore */ } setRecording(false); };

  return (
    <div style={{ marginTop: 'var(--spacing-3)', padding: 'var(--spacing-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-surface-elevated)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-2)' }}>
        <strong style={{ fontSize: 'var(--font-size-sm)' }}>{title}</strong>
        {items.length > 0 && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{done}/{items.length} done</span>}
      </div>
      {isLoading ? (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{kind === 'defect' ? 'No defects logged — add each issue to fix on the call back.' : 'No items yet — add the work to be done, line by line.'}</div>
      ) : (
        items.map((it) => (
          <div key={it.id} style={row}>
            <input type="checkbox" checked={it.done} onChange={(e) => upd.mutate({ id: it.id, patch: { done: e.target.checked } })} />
            <span style={{ flex: 1, fontSize: 'var(--font-size-sm)', textDecoration: it.done ? 'line-through' : 'none', color: it.done ? 'var(--color-text-muted)' : 'var(--color-text)' }}>{it.body}</span>
            <button style={smallBtn} title="Remove" onClick={() => del.mutate(it.id)}>✕</button>
          </div>
        ))
      )}
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-2)' }}>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={`Add ${noun}…`} style={input}
          onKeyDown={(e) => { if (e.key === 'Enter' && text.trim()) add.mutate(text.trim()); }} />
        <button
          disabled={!text.trim() || add.isPending}
          onClick={() => add.mutate(text.trim())}
          style={{ padding: 'var(--spacing-2) var(--spacing-3)', border: '1px solid var(--color-accent)', borderRadius: 'var(--radius-default)', background: 'var(--color-accent)', color: 'var(--color-text-inverse)', fontWeight: 600, cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}
        >Add</button>
      </div>
      {kind === 'scope' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-2)', flexWrap: 'wrap' }}>
          {!recording ? (
            <button onClick={startRec} disabled={transcribing} style={voiceBtn}>🎤 {transcribing ? 'Transcribing…' : 'Record scope'}</button>
          ) : (
            <button onClick={stopRec} style={{ ...voiceBtn, borderColor: 'var(--color-error)', color: 'var(--color-error)' }}>⏹ Stop &amp; add</button>
          )}
          {recording ? (
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error)' }}>● Recording… speak each task</span>
          ) : !transcribing ? (
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>or record a note — we’ll turn it into items</span>
          ) : null}
        </div>
      )}
      {voiceErr && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error)', marginTop: 4 }}>{voiceErr}</div>}
    </div>
  );
}
